"use client";

/**
 * Liste interactive des laboratoires d'une ville ou d'un quartier : filtres,
 * tri par distance (géolocalisation du navigateur, jamais transmise au
 * serveur), carte à la demande. La liste complète est rendue côté serveur
 * (SEO) ; les filtres ne font que la restreindre.
 *
 * Accessibilité : titre de liste (masqué), groupe de filtres nommé, bouton de
 * carte avec aria-expanded, UNE zone de statut permanente (nombre de résultats,
 * tri, refus de localisation). « Ouvert maintenant » est recalculé chaque
 * minute tant que le filtre est actif.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { LabCard, type LabCardData } from "@/components/labs/LabCard";
import { hasKnownHours, isOpenAt, opensOnSunday } from "@/lib/labs/hours";
import { haversineKmPure } from "@/lib/labs/geo";

const LabMapView = dynamic(() => import("@/components/labs/LabMapView").then((m) => m.LabMapView), {
  ssr: false,
  loading: () => <div className="h-[380px] w-full animate-pulse rounded-[18px] bg-[var(--sahha-surface-1)]" aria-hidden="true" />,
});

type FilterKey = "openNow" | "sunday" | "h24" | "homeSampling" | "cnops" | "cnss";
const FILTERS: FilterKey[] = ["openNow", "sunday", "h24", "homeSampling", "cnops", "cnss"];
type Props = { labs: LabCardData[]; centerLat: number; centerLng: number; locale: string };

function match(l: LabCardData, k: FilterKey, now: Date): boolean {
  switch (k) {
    case "openNow": return isOpenAt(l, now);
    case "sunday": return opensOnSunday(l) === true;
    case "h24": return l.open24h;
    case "homeSampling": return l.homeSampling === true;
    case "cnops": return l.conventions.includes("CNOPS");
    case "cnss": return l.conventions.includes("CNSS");
  }
}

const applyFilters = (labs: LabCardData[], active: ReadonlySet<FilterKey>, now: Date) =>
  labs.filter((l) => [...active].every((k) => match(l, k, now)));

export function LabExplorer({ labs, centerLat, centerLng, locale }: Props) {
  const t = useTranslations("labs");
  const id = useId();
  const [active, setActive] = useState<Set<FilterKey>>(() => new Set());
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null);
  const [geoMsg, setGeoMsg] = useState<string | null>(null);
  // Dernière annonce : le nombre de résultats (recalculé au rendu) ou un message de tri / localisation.
  const [announce, setAnnounce] = useState<{ kind: "count" } | { kind: "msg"; text: string } | null>(null);
  const [locating, setLocating] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [listMinHeight, setListMinHeight] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const openNowOn = active.has("openNow");

  // « Ouvert maintenant » : la liste suit l'heure (une fiche qui ferme en sort), dès l'activation puis chaque minute.
  useEffect(() => {
    if (!openNowOn) return;
    const tick = () => setNow(Date.now());
    tick();
    const iv = window.setInterval(tick, 60_000);
    return () => window.clearInterval(iv);
  }, [openNowOn]);

  // Un filtre n'apparaît que si au moins un laboratoire peut y répondre (horaires connus pour « ouvert maintenant »).
  const available = useMemo(
    () => FILTERS.filter((k) => (k === "openNow" ? labs.some(hasKnownHours) : labs.some((l) => match(l, k, new Date(0))))),
    [labs],
  );
  const unknownHours = useMemo(() => labs.filter((l) => !hasKnownHours(l)).length, [labs]);
  const pinned = useMemo(() => labs.filter((l) => l.lat != null && l.lng != null).length, [labs]);

  const shown = useMemo(() => {
    let out = applyFilters(labs, active, new Date(now));
    if (pos) {
      out = out
        .map((l) => ({ ...l, distanceKm: l.lat != null && l.lng != null ? haversineKmPure(pos.lat, pos.lng, l.lat, l.lng) : null }))
        .sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
    }
    return out;
  }, [labs, active, pos, now]);

  const update = (next: Set<FilterKey>) => {
    setActive(next);
    setListMinHeight(null);
    setAnnounce({ kind: "count" });
  };
  const toggle = (k: FilterKey) => {
    const next = new Set(active);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    update(next);
  };

  const say = (m: string) => { setGeoMsg(m); setAnnounce({ kind: "msg", text: m }); };
  const locate = () => {
    if (locating) return;
    if (!("geolocation" in navigator)) { say(t("sort.unavailable")); return; }
    // Le tri arrive après la géolocalisation (souvent > 500 ms) : on fige la hauteur
    // de la liste et on la remplace d'un bloc, sans décaler la suite de la page.
    setListMinHeight(listRef.current?.offsetHeight ?? null);
    setLocating(true);
    setGeoMsg(null);
    setAnnounce({ kind: "msg", text: t("sort.locating") });
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        setLocating(false);
        say(pinned < labs.length ? t("sort.sortedPartial") : t("sort.sorted"));
      },
      (e) => {
        setLocating(false);
        setListMinHeight(null);
        say(e.code === e.PERMISSION_DENIED ? t("sort.denied") : e.code === e.TIMEOUT ? t("sort.timeout") : t("sort.unavailable"));
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    );
  };

  return (
    <section aria-labelledby={`${id}-title`} className="space-y-4">
      <h2 id={`${id}-title`} className="sr-only">{t("filters.listTitle")}</h2>
      {available.length > 0 && (
        <div role="group" aria-label={t("filters.label")} className="flex flex-wrap items-center gap-2">
          {available.map((k) => (
            <button key={k} type="button" aria-pressed={active.has(k)} onClick={() => toggle(k)}
              className={`rounded-full px-3.5 py-1.5 text-[13.5px] font-semibold transition-colors ${active.has(k)
                ? "bg-[var(--sahha-primary-700)] text-white"
                : "bg-[var(--sahha-surface-0)] text-[var(--sahha-text-800)] ring-1 ring-[var(--sahha-border-default)] hover:bg-[var(--sahha-surface-1)]"}`}>
              {t(`filters.${k}`)}
            </button>
          ))}
          {active.size > 0 && (
            <button type="button" onClick={() => update(new Set())} className="px-2 py-1.5 text-[13px] font-semibold text-[var(--sahha-primary-700)] underline-offset-2 hover:underline">
              {t("filters.reset")}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] font-semibold text-[var(--sahha-text-700)]">{t("filters.count", { count: shown.length })}</p>
        <div className="flex flex-wrap gap-2">
          {pinned > 0 && (
            <button type="button" onClick={locate} aria-disabled={locating}
              className="rounded-full px-3.5 py-1.5 text-[13.5px] font-semibold text-[var(--sahha-primary-800)] ring-1 ring-[var(--sahha-primary-200)] hover:bg-[var(--sahha-primary-50)] aria-disabled:opacity-60">
              {locating ? t("sort.locating") : t("sort.nearest")}
            </button>
          )}
          {pinned > 0 && (
            <button type="button" onClick={() => setShowMap((v) => !v)} aria-expanded={showMap} aria-controls={`${id}-map`}
              className="rounded-full px-3.5 py-1.5 text-[13.5px] font-semibold text-[var(--sahha-text-800)] ring-1 ring-[var(--sahha-border-default)] hover:bg-[var(--sahha-surface-1)]">
              {showMap ? t("map.hide") : t("map.show", { count: pinned })}
            </button>
          )}
        </div>
      </div>

      {/* Zone de statut unique, toujours présente : annoncée par les lecteurs d'écran. */}
      <p role="status" className="sr-only">{announce?.kind === "count" ? t("filters.count", { count: shown.length }) : announce?.text ?? ""}</p>
      {/* Ligne réservée dès le clic (saisie utilisateur, hors CLS) : le message arrive
          après la géolocalisation (> 500 ms) sans décaler la carte ni la liste. */}
      {(locating || geoMsg) && (
        <p aria-hidden="true" className="min-h-[1.25rem] text-[13px] text-[var(--sahha-text-600)]">{locating ? "" : geoMsg}</p>
      )}
      {openNowOn && unknownHours > 0 && (
        <p className="text-[13px] text-[var(--sahha-text-600)]">{t("filters.unknownHoursNote", { count: unknownHours })}</p>
      )}

      {/* Trié par distance, la carte porte le repère du visiteur et l'ordre de la liste le situe :
          carte exclue de l'enregistrement de session (ph-no-capture), textes de la liste masqués. */}
      <div id={`${id}-map`} className={pos ? "ph-no-capture" : undefined}>
        {showMap && (
          <div className="space-y-1.5">
            <LabMapView labs={shown} centerLat={centerLat} centerLng={centerLng} userPos={pos} />
            {pinned < labs.length && <p className="text-[12.5px] text-[var(--sahha-text-500)]">{t("map.onlyPrecise")}</p>}
          </div>
        )}
      </div>

      <div ref={listRef} data-ph-mask={pos ? "" : undefined} style={listMinHeight ? { minHeight: listMinHeight } : undefined}>
        {shown.length === 0 ? (
          <p className="rounded-[18px] bg-[var(--sahha-surface-1)] p-5 text-[14px] text-[var(--sahha-text-700)]">{t("filters.noneMatch")}</p>
        ) : (
          // Nouvelle clé au tri par distance : liste remplacée d'un bloc (pas de cartes « déplacées » comptées dans le CLS).
          <ul key={pos ? `${pos.lat},${pos.lng}` : "default"} className="grid gap-3 sm:grid-cols-2">
            {shown.map((l) => <li key={l.slug}><LabCard lab={l} locale={locale} /></li>)}
          </ul>
        )}
      </div>
    </section>
  );
}
